import React from "react";
import { Heart, Plus, Layers, Gem } from "lucide-react";
import { ScoreRing, StarScore } from "@/components/ScoreRing";
import { ToolLogo } from "@/components/ToolLogo";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";

export function ToolCard({ tool, onCompareToggle, isCompared, isFav, onFavToggle, isPinned, onPinToggle, compact = false }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const toggleFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { window.location.href = "/login"; return; }
    await api.post("/favorites/toggle", { tool_slug: tool.slug });
    onFavToggle?.(tool.slug);
    refresh();
  };

  const togglePin = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { window.location.href = "/login"; return; }
    try {
      await api.post("/stack/pin", { tool_slug: tool.slug });
      onPinToggle?.(tool.slug);
      refresh();
    } catch (err) {
      // Surface simple limit errors gracefully
      const msg = err?.response?.data?.detail || "Couldn't update your stack.";
      alert(msg);
    }
  };

  if (compact) {
    const grade = tool.letter_grade;
    const stars5 = Math.max(0, Math.min(5, Math.round(tool.user_score || 0)));
    const goToDetail = () => navigate(`/tool/${tool.slug}`);
    const onCardKey = (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToDetail(); }
    };
    return (
      <div
        role="link"
        tabIndex={0}
        onClick={goToDetail}
        onKeyDown={onCardKey}
        className="group block relative rounded-2xl bg-white border border-[rgba(220,226,236,0.9)] p-3.5 hover:shadow-lg hover:-translate-y-0.5 hover:border-[rgba(241,115,79,0.35)] transition-all overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B1220]/40"
        data-testid={`tool-card-${tool.slug}`}
        aria-label={`Open ${tool.name} details`}
      >
        <div className="flex items-start gap-2.5 mb-2">
          <ToolLogo tool={tool} size={34} rounded="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-display font-semibold text-[14.5px] leading-tight text-[#0B1220] truncate">{tool.name}</span>
              {tool.hidden_gem && (
                <Gem size={11} className="text-[#F97316] flex-shrink-0" data-testid={`gem-${tool.slug}`} aria-label="Hidden Gem" />
              )}
            </div>
            <div className="text-[9.5px] uppercase tracking-wider text-[#8A93A6] mt-0.5 truncate">{tool.category}</div>
          </div>
          {grade && (
            <span className="text-[9px] font-bold text-white bg-[#0B1220] px-1.5 py-0.5 rounded flex-shrink-0" title={`Wikifi grade ${grade}`}>{grade}</span>
          )}
        </div>

        <p className="text-[11px] text-[#4B5568] leading-snug line-clamp-2 mb-2 min-h-[28px]">{tool.tagline || "AI tool built to accelerate your workflow."}</p>

        {(tool.pros?.length || tool.cons?.length) ? (
          <div className="grid grid-cols-2 gap-1.5 mb-2 text-[10px]">
            <div>
              <div className="text-[8.5px] font-bold tracking-wider text-emerald-700 uppercase mb-0.5">Pros</div>
              <ul className="space-y-0.5 leading-tight">
                {(tool.pros || []).slice(0, 2).map((p) => (
                  <li key={p} className="text-[#3D465C] truncate">+ {p}</li>
                ))}
                {!(tool.pros || []).length && <li className="text-[#8A93A6]">—</li>}
              </ul>
            </div>
            <div>
              <div className="text-[8.5px] font-bold tracking-wider text-rose-700 uppercase mb-0.5">Cons</div>
              <ul className="space-y-0.5 leading-tight">
                {(tool.cons || []).slice(0, 2).map((c) => (
                  <li key={c} className="text-[#3D465C] truncate">– {c}</li>
                ))}
                {!(tool.cons || []).length && <li className="text-[#8A93A6]">—</li>}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-[rgba(220,226,236,0.8)] pt-2">
          <div className="flex items-center gap-0.5" data-testid={`stars-${tool.slug}`}>
            {[1,2,3,4,5].map(i => (
              <svg key={i} width="11" height="11" viewBox="0 0 24 24" className={i <= stars5 ? "fill-[#F97316]" : "fill-[#E2E8F0]"}>
                <path d="M12 2l2.9 6.9L22 10l-5.5 4.7L18.2 22 12 18l-6.2 4 1.7-7.3L2 10l7.1-1.1z"/>
              </svg>
            ))}
            <span className="text-[10px] text-[#8A93A6] ml-1">{tool.user_votes ? `${(tool.user_votes/1000).toFixed(1)}k` : ""}</span>
          </div>
          {tool.url ? (
            <a
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[11px] font-semibold text-[#F97316] hover:text-[#EA6A0A] transition-colors flex items-center gap-1"
              data-testid={`visit-${tool.slug}`}
              title={`Open ${tool.name} in a new tab`}
            >
              Visit
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M8 7h9v9"/></svg>
            </a>
          ) : (
            <span className="text-[10px] text-[#8A93A6]">No link</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Link to={`/tool/${tool.slug}`} className="block relative overflow-hidden curio-card p-6"
          data-testid={`tool-card-${tool.slug}`}>
      {tool.rank ? (
        <div
          className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-[var(--curio-navy)] text-white text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 shadow-sm z-10"
          data-testid={`rank-badge-${tool.slug}`}
          title={`Ranked #${tool.rank} by monthly visits`}
        >
          <span className="opacity-60">#</span>{tool.rank}
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <ToolLogo tool={tool} size={48} rounded="xl" />
          <div>
            <h3 className="font-display text-2xl font-semibold leading-tight text-[#0B1220] flex items-center gap-1.5">
              {tool.name}
              {tool.hidden_gem && (
                <Gem size={15} className="text-[#F97316] flex-shrink-0" data-testid={`gem-${tool.slug}`} aria-label="Hidden Gem" />
              )}
            </h3>
            <div className="text-xs uppercase tracking-widest text-[#5B6478] mt-0.5 font-semibold">{tool.category}</div>
          </div>
        </div>
        <div className={`flex items-center gap-0.5 ${tool.rank ? 'mt-6' : ''}`}>
          <button onClick={togglePin} className={`transition-colors p-1.5 ${isPinned ? 'text-[var(--curio-navy)]' : 'text-[#5B6478] hover:text-[var(--curio-navy)]'}`}
                  data-testid={`stack-${tool.slug}`} aria-label={isPinned ? "Remove from stack" : "Add to stack"} title={isPinned ? "In your stack" : "Add to your stack"}>
            <Layers size={17} fill={isPinned ? "currentColor" : "none"} stroke="currentColor" />
          </button>
          <button onClick={toggleFav} className="text-[#5B6478] hover:text-[#F97316] transition-colors p-1.5"
                  data-testid={`fav-${tool.slug}`} aria-label="Favorite">
            <Heart size={18} fill={isFav ? "#F97316" : "none"} stroke={isFav ? "#F97316" : "currentColor"} />
          </button>
        </div>
      </div>

      <p className="text-sm text-[#0B1220]/85 leading-relaxed mb-4 line-clamp-2">{tool.tagline}</p>

      {(tool.pros?.length || tool.cons?.length) ? (
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <div>
            <div className="text-[10px] font-bold tracking-widest text-emerald-700 uppercase mb-1">Pros</div>
            <ul className="space-y-0.5">{(tool.pros || []).slice(0,2).map((p)=>(<li key={p} className="text-[#0B1220]/80">+ {p}</li>))}</ul>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-widest text-rose-700 uppercase mb-1">Cons</div>
            <ul className="space-y-0.5">{(tool.cons || []).slice(0,2).map((p)=>(<li key={p} className="text-[#0B1220]/80">– {p}</li>))}</ul>
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between border-t border-[var(--curio-border)] pt-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <ScoreRing
              score={tool.value_score != null ? Math.round((tool.curio_score + tool.value_score) / 2) : tool.curio_score}
              label="Score"
            />
            {tool.letter_grade && <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-[var(--curio-coral)] text-white rounded-md px-1 leading-tight">{tool.letter_grade}</span>}
          </div>
          <StarScore score={tool.user_score} votes={tool.user_votes} />
          <span
            className={`text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border ${
              tool.pricing === 'Free'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : tool.pricing === 'Paid'
                  ? 'bg-rose-50 border-rose-200 text-rose-800'
                  : 'bg-[var(--curio-bg)] border-[var(--curio-border)] text-[var(--curio-text)]'
            }`}
            data-testid={`pricing-${tool.slug}`}
          >
            {tool.pricing}
          </span>
        </div>
        <div className="flex gap-1.5 flex-wrap justify-end">
          <button onClick={(e)=>{e.preventDefault(); e.stopPropagation(); onCompareToggle?.(tool);}}
                  className={`text-xs rounded-full px-2.5 py-1 border transition-all ${isCompared ? 'bg-[var(--curio-navy)] text-white border-[var(--curio-navy)]' : 'border-[var(--curio-border)] hover:border-[var(--curio-navy)]'}`}
                  data-testid={`compare-${tool.slug}`}>
            <Plus size={11} className="inline -mt-0.5"/> Compare
          </button>
        </div>
      </div>
    </Link>
  );
}
